import type { ReactNode } from "react";
import { AppHeader } from "./AppHeader";
import { cn } from "../lib/cn";
import { ACCENTS, type AccentName } from "../lib/accents";
import { PAGE_GUTTER, PAGE_WIDTH } from "../lib/layout";
import { FeatureSlot } from "../features/_sim/Slot"; // SIM-SEAM: mobile bottom nav

/**
 * Page widths (lib/layout.ts holds the rule). The Tailwind-sounding keys are
 * kept so existing pages compile unchanged: "5xl" and "wide" are the wide
 * layout, the rest are reading widths.
 */
const MAX_W = {
  xl: PAGE_WIDTH.compact,
  "2xl": PAGE_WIDTH.narrow,
  "3xl": PAGE_WIDTH.reading,
  "4xl": PAGE_WIDTH.article,
  "5xl": PAGE_WIDTH.wide,
  wide: PAGE_WIDTH.wide,
} as const;

// Second and third colour fields of the backdrop, per tone (literal classes).
// The first one is the tone's own ACCENTS[tone].glow.
const BACKDROP_FIELDS: Record<AccentName, [string, string]> = {
  sky: ["bg-violet-200/40", "bg-rose-200/35"],
  rose: ["bg-amber-200/35", "bg-violet-200/30"],
  amber: ["bg-rose-200/35", "bg-sky-200/35"],
  emerald: ["bg-sky-200/40", "bg-amber-100/50"],
  violet: ["bg-sky-200/40", "bg-rose-200/30"],
  indigo: ["bg-sky-200/40", "bg-violet-200/35"],
};

interface AppShellProps {
  /** Page width. Default "3xl" (reading); pass "5xl"/"wide" for dashboards and lists. */
  maxWidth?: keyof typeof MAX_W;
  /** Hue of the page's soft scene (gradient wash + colour fields). Default `sky`. */
  tone?: AccentName;
  /** Optional full-width band rendered between the header and <main>. */
  hero?: ReactNode;
  children: ReactNode;
}

/**
 * The page's scene, like one landing-page scene stretched down the page: a
 * vertical pastel gradient, three blurred colour fields near the top (where
 * the hero sits) and one low on the page, and a whisper of grain. Absolutely
 * positioned (not fixed) so full-page captures and print see what scrolls.
 */
function Backdrop({ tone }: { tone: AccentName }) {
  const [second, third] = BACKDROP_FIELDS[tone];
  return (
    <div
      aria-hidden="true"
      className="no-print pointer-events-none absolute inset-0 -z-10 overflow-hidden"
    >
      <div className={cn("absolute inset-0 bg-gradient-to-b", ACCENTS[tone].scene)} />
      <div
        className={cn(
          "absolute -left-40 -top-40 h-[30rem] w-[30rem] rounded-full blur-3xl",
          ACCENTS[tone].glow,
        )}
      />
      <div
        className={cn(
          "absolute -right-32 top-24 h-[26rem] w-[26rem] rounded-full blur-3xl",
          second,
        )}
      />
      <div
        className={cn(
          "absolute left-1/3 top-[32rem] h-72 w-72 rounded-full blur-3xl",
          third,
        )}
      />
      <div
        className={cn(
          "absolute -bottom-24 -right-24 h-96 w-96 rounded-full blur-3xl",
          ACCENTS[tone].glow,
        )}
      />
      <div className="bg-grain absolute inset-0 opacity-[0.05] mix-blend-multiply" />
    </div>
  );
}

/** Shared scaffold for the logged-in pages: scene backdrop + header + optional hero + centered main. */
export function AppShell({ maxWidth = "3xl", tone = "sky", hero, children }: AppShellProps) {
  return (
    <div className="relative isolate min-h-screen bg-white">
      <Backdrop tone={tone} />
      <AppHeader />
      {hero}
      <main className={cn("mx-auto w-full pb-16 pt-8 sm:pt-12", PAGE_GUTTER, MAX_W[maxWidth])}>
        {children}
      </main>
      {/* SIM-SEAM: mobile bottom tab bar (+ its spacer) */}
      <FeatureSlot name="shell.bottomNav" />
    </div>
  );
}
